import React, { useEffect, useMemo, useRef } from 'react'
import cx from 'clsx'
import { MenuItem, MenuButton, ControlledMenu, useMenuState, useClick } from '@szhsin/react-menu'
import { NavLink, generatePath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ContentLock from '@components/ContentLock'
import OrganizationMenu from '../OrganizationMenu'
import Divider from '@components/Divider'
import type { TablerIconsProps } from '@tabler/icons-react'
import { IconMenu2, IconX } from '@tabler/icons-react'

import type { NavItem } from '../menuItems'
import { NAV_ITEMS, AP_ITEMS, AR_ITEMS, CONTRACT_ITEMS } from '../menuItems'
import { useGetAccount } from '@helpers/hooks/useGetAccount'
import type { FilteredNavItem } from '../utils'
import { filterNavItems } from '../utils'
import routes from '@src/Routes/routes'
import { useGetShowCustomers } from '@helpers/hooks/useGetShowCustomers'

import { ContactParty } from '@models/ApproveitAccounting'

import styles from './index.module.css'
import { twMerge } from 'tailwind-merge'

// Plan-locked entries stay in the menu, locked and pointing at the paywall.
const INCLUDE_LOCKED = { includeLocked: true }

interface NavigationLinkProps extends FilteredNavItem<NavItem> {
  to?: string
  label: string
  icon: React.FC<TablerIconsProps>
  isDisabled?: boolean
}

const NavigationLink: React.FC<NavigationLinkProps> = ({
  to,
  locked,
  paywallPath,
  lockTooltip,
  icon: Icon,
  label,
  isDisabled = false,
}) => {
  if (!to) return null

  // Same rule as LeftNav: a plan lock disables the row, a lock with no
  // paywall behind it (the assistant's beta lock) dims it but stays clickable.
  const planLocked = locked && !!paywallPath
  return (
    <MenuItem className="px-3 py-0.5 bg-transparent">
      <NavLink
        className={({ isActive }) =>
          cx(
            styles.leftNavLink,
            (isActive || (window.location.pathname === '/' && to === '/dashboard')) && styles.active,
            'flex items-center justify-between px-2 group',
            (isDisabled || planLocked) && styles.disabled,
          )
        }
        to={planLocked ? paywallPath : to}
      >
        <div className={cx('flex items-center gap-5', locked && 'opacity-60')}>
          <Icon className={styles.navIcon} stroke={1.5} />
          <span>{label}</span>
        </div>
        {locked && <ContentLock tooltipText={paywallPath ? undefined : lockTooltip} />}
      </NavLink>
    </MenuItem>
  )
}
const NavigationBlock = ({
  items,
  title,
  className,
}: {
  items: FilteredNavItem<NavItem>[]
  title?: string
  className?: string
}) => {
  // Same rule as LeftNav: the group is locked when every entry in it is.
  const groupLocked = items.length > 0 && items.every((item) => item.locked)
  const groupPaywallPath = groupLocked ? items[0].paywallPath : undefined
  return items.length ? (
    <div className={twMerge(cx('pt-1 flex flex-col text-nowrap', className))}>
      {title && (
        <div
          className={cx(
            'flex justify-between items-center pl-5 pr-5 text-sm font-medium text-grey_1 transition-opacity duration-400 mb-1',
          )}
        >
          {title}
          {groupPaywallPath && <ContentLock to={groupPaywallPath} />}
        </div>
      )}
      {items.map((item) => (
        <NavigationLink key={item.to} {...item} />
      ))}
    </div>
  ) : null
}
const BurgerMenu: React.FC<{ isDisabled?: boolean }> = ({ isDisabled }) => {
  const { t } = useTranslation('common', { keyPrefix: 'navigation' })

  const menuRef = useRef(null)
  const [menuState, toggleMenu] = useMenuState({ transition: { open: true } })

  const showCustomers = useGetShowCustomers()
  const account = useGetAccount()

  // Memoized like LeftNav's commonItems — the filter otherwise re-runs on
  // every render, including menu open/close toggles.
  const commonItems = useMemo(
    () => (account ? filterNavItems(NAV_ITEMS, account, undefined, INCLUDE_LOCKED) : []),
    [account],
  )

  const anchorProps = useClick(menuState.state, toggleMenu)

  useEffect(() => {
    const container = document.getElementById('container')

    if (container) {
      if (menuState.state === 'open') {
        document.body.style.overflow = 'hidden'
        container.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
        container.style.overflow = 'auto'
      }
    }

    return () => {
      document.body.style.overflow = 'auto'
      if (container) {
        container.style.overflow = 'auto'
      }
    }
  }, [menuState.state])

  return (
    <>
      <MenuButton ref={menuRef} {...anchorProps}>
        <div className="relative group">
          <div
            className={cx(
              styles.burgerMenuButton,
              'group-hover:opacity-10 group-active:opacity-20 group-active:outline',
            )}
          />
          <IconMenu2 className="relative z-10 h-6 w-6 text-black" stroke={1.5} />
        </div>
      </MenuButton>
      <ControlledMenu menuClassName="!-top-52" {...menuState} anchorRef={menuRef} onClose={() => toggleMenu(false)}>
        <div
          className={styles.burgerMenu}
          style={{
            opacity: menuState.state === 'open' ? 1 : 0,
            bottom: menuState.state === 'open' ? 0 : '-100vh',
          }}
        >
          <div
            id="navigation"
            className="flex items-center justify-between px-4 h-12 bg-white_smoke z-10 relative border-t xs:hidden"
          >
            <MenuItem className={styles.companyLogoWrap}>
              <NavLink to={routes.private.dashboard}>
                <img className={styles.companyLogo} src="/logo.svg" />
                Approveit
              </NavLink>
            </MenuItem>
            <IconX size={1} className="h-5 w-5 cursor-pointer text-brand_primary" onClick={() => toggleMenu(false)} />
          </div>
          <OrganizationMenu isBurgerMenu align="end" direction="bottom" />
          <Divider className="my-1" />
          {commonItems.map((item) => (
            <NavigationLink key={item.to} {...item} isDisabled={isDisabled} />
          ))}

          <NavigationBlock
            items={filterNavItems(
              AP_ITEMS,
              account,
              { [generatePath(routes.private.contacts, { contactParty: ContactParty.vendor })]: showCustomers },
              INCLUDE_LOCKED,
            )}
            title={t('accountsPayable')}
          />
          <NavigationBlock
            items={filterNavItems(
              AR_ITEMS,
              account,
              { [generatePath(routes.private.contacts, { contactParty: ContactParty.customer })]: showCustomers },
              INCLUDE_LOCKED,
            )}
            title={t('accountsReceivable')}
          />
          {account &&
            filterNavItems(CONTRACT_ITEMS, account, undefined, INCLUDE_LOCKED).map((item) => (
              <NavigationLink key={item.to} {...item} isDisabled={isDisabled} />
            ))}
        </div>
      </ControlledMenu>
    </>
  )
}

export default BurgerMenu
